import ShowRow from '../components/ShowRow';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';
import { getAnalytics, logEvent } from 'firebase/analytics';
import {
    AddPhotoAlternateRounded,
    ArrowBackRounded,
    CheckRounded,
    ChevronLeftRounded,
    ChevronRightRounded,
    CloseRounded,
    ErrorOutlineRounded,
} from '@mui/icons-material';

import BottomMenu from '../components/BottomMenu';
import Loading from '../components/Loading';
import {
    invalidateLatestSubscribed,
    syncSubscribeStatuses,
} from '../redux/actions/userActions';
import {
    getPaidEndpointAppCheckToken,
    validTokenExists,
} from '../firebaseHelper';
import {
    convertImageFileToJpeg,
    jpegFilenameFor,
    MAX_LIBRARY_SCREENSHOTS,
} from '../utils/libraryScreenshot';

const IMPORT_SCREENSHOT_EXAMPLES = [
    {
        name: 'Spotify',
        screenshot: '/import_examples/shows_spotify.jpg',
        appIcon: '/import_examples/app_spotify.png',
        roundAppIcon: true,
    },
    {
        name: 'Pocket Casts',
        screenshot: '/import_examples/shows_pocket_casts.jpg',
        appIcon: '/import_examples/app_pocket_casts.png',
        roundAppIcon: true,
    },
    {
        name: 'Overcast',
        screenshot: '/import_examples/shows_overcast.jpg',
        appIcon: '/import_examples/app_overcast.png',
        softlyRoundedAppIcon: true,
    },
    {
        name: 'Castbox',
        screenshot: '/import_examples/shows_castbox.jpg',
        appIcon: '/import_examples/app_castbox.png',
    },
    {
        name: 'Apple Podcasts',
        screenshot: '/import_examples/shows_apple_podcasts.jpg',
        appIcon: '/import_examples/app_apple_podcasts.png',
    },
];

const uploadErrorMessage = (error) => (
    error?.response?.data?.error ||
    error?.message ||
    'Could not import shows from this screenshot.'
);

export const ImportShows = ({
    authenticated = null,
    invalidateLatestSubscribed,
    syncSubscribeStatuses,
}) => {
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const examplesRef = useRef(null);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [selectionError, setSelectionError] = useState('');
    const [uploadErrors, setUploadErrors] = useState([]);
    const [importedPodcasts, setImportedPodcasts] = useState([]);
    const [unknownPodcastNames, setUnknownPodcastNames] = useState([]);
    const [processedCount, setProcessedCount] = useState(0);
    const [uploadTotal, setUploadTotal] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [finished, setFinished] = useState(false);
    const [nothingFound, setNothingFound] = useState(false);

    useEffect(() => {
        if (authenticated === true) {
            logEvent(getAnalytics(), 'screen_view', {
                firebase_screen: 'import_shows',
            });
        }
    }, [authenticated]);

    const clearResults = () => {
        setUploadErrors([]);
        setImportedPodcasts([]);
        setUnknownPodcastNames([]);
        setProcessedCount(0);
        setUploadTotal(0);
        setFinished(false);
        setNothingFound(false);
    };

    const onFilesSelected = (event) => {
        const nextFiles = Array.from(event.target.files || []);
        event.target.value = '';

        if (nextFiles.length > MAX_LIBRARY_SCREENSHOTS) {
            setSelectedFiles([]);
            setSelectionError(`Choose up to ${MAX_LIBRARY_SCREENSHOTS} screenshots.`);
            return;
        }
        if (nextFiles.some((file) => !file.type.startsWith('image/'))) {
            setSelectedFiles([]);
            setSelectionError('Choose image screenshots.');
            return;
        }

        setSelectionError('');
        setSelectedFiles(nextFiles);
    };

    const removeSelectedFile = (indexToRemove) => {
        setSelectedFiles((currentFiles) => (
            currentFiles.filter((file, index) => index !== indexToRemove)
        ));
        setSelectionError('');
    };

    const scrollExamples = (direction) => {
        examplesRef.current?.scrollBy({
            left: direction * 324,
            behavior: 'smooth',
        });
    };

    const uploadScreenshots = async () => {
        if (selectedFiles.length === 0 || uploading) {
            setSelectionError('Choose at least one screenshot.');
            return;
        }

        clearResults();
        setSelectionError('');
        setUploading(true);
        setUploadTotal(selectedFiles.length);

        const importedByKey = new Map();
        const importedNameKeys = new Set();
        const unknownNamesByKey = new Map();
        const nextUploadErrors = [];

        try {
            await validTokenExists();
            const appCheckToken = await getPaidEndpointAppCheckToken();

            for (let index = 0; index < selectedFiles.length; index += 1) {
                const file = selectedFiles[index];
                try {
                    const jpegBlob = await convertImageFileToJpeg(file);
                    const formData = new FormData();
                    formData.append('imageData', jpegBlob, jpegFilenameFor(file.name, index));
                    const response = await axios.post('/upload_library_screenshot', formData, {
                        headers: {
                            'X-Firebase-AppCheck': appCheckToken,
                        },
                    });

                    const successfulImports = Array.isArray(response.data?.successfulImports)
                        ? response.data.successfulImports
                        : [];
                    const importedStatuses = successfulImports
                        .filter(podcast => podcast?.sourceId)
                        .map(podcast => ({ subscribeId: podcast.sourceId, subscribeStatus: true }));
                    if (importedStatuses.length > 0) {
                        // Acknowledge before exposing editable rows; later uploads must not
                        // reapply this result over a preference changed in the meantime.
                        syncSubscribeStatuses({ podcastSubscribeStatuses: importedStatuses, acknowledged: true });
                    }
                    successfulImports.forEach((podcast) => {
                        const key = podcast?.sourceId || podcast?.name;
                        if (key) {
                            importedByKey.set(key, podcast);
                        }
                        const nameKey = typeof podcast?.name === 'string'
                            ? podcast.name.trim().toLocaleLowerCase()
                            : '';
                        if (nameKey) {
                            importedNameKeys.add(nameKey);
                            unknownNamesByKey.delete(nameKey);
                        }
                    });

                    const unknowns = Array.isArray(response.data?.unknownPodcastNames)
                        ? response.data.unknownPodcastNames
                        : [];
                    unknowns.forEach((name) => {
                        if (typeof name === 'string' && name.trim()) {
                            const trimmedName = name.trim();
                            const nameKey = trimmedName.toLocaleLowerCase();
                            if (!importedNameKeys.has(nameKey)) {
                                unknownNamesByKey.set(nameKey, trimmedName);
                            }
                        }
                    });
                } catch (error) {
                    nextUploadErrors.push({
                        filename: file.name,
                        message: uploadErrorMessage(error),
                    });
                }

                setImportedPodcasts(Array.from(importedByKey.values()));
                setUnknownPodcastNames(Array.from(unknownNamesByKey.values()));
                setUploadErrors([...nextUploadErrors]);
                setProcessedCount(index + 1);
            }

            if (importedByKey.size > 0) {
                invalidateLatestSubscribed();
            }

            setNothingFound(
                importedByKey.size === 0 &&
                unknownNamesByKey.size === 0 &&
                nextUploadErrors.length === 0,
            );
            setFinished(true);
            setSelectedFiles([]);
        } catch (error) {
            setUploadErrors([{
                filename: '',
                message: uploadErrorMessage(error),
            }]);
        } finally {
            setUploading(false);
        }
    };

    if (authenticated === false) {
        return <Navigate to='/sign_in?redirect=%2Fimport_shows' replace />;
    }

    if (authenticated !== true) {
        return (
            <div className='container appScreen'>
                <Loading />
            </div>
        );
    }

    const progressText = uploading
        ? `Processing ${Math.min(processedCount + 1, uploadTotal)} of ${uploadTotal}`
        : finished
            ? 'Finished'
            : '';

    return (
        <div className='container appScreen hasBottomMenu importShowsPage'>
            <main className='importShowsContent'>
                <header className='importShowsHeader'>
                    <button
                        type='button'
                        className='importShowsBackButton'
                        aria-label='Back to Library'
                        onClick={() => navigate('/library')}
                    >
                        <ArrowBackRounded aria-hidden='true' />
                    </button>
                    <h1 className='largeHeading importShowsTitle'>Import shows</h1>
                </header>

                <section className='importShowsPickerCard'>
                    <div className='importShowsPickerIcon'>
                        <AddPhotoAlternateRounded aria-hidden='true' />
                    </div>
                    <div className='importShowsPickerCopy'>
                        <h2>Screenshots</h2>
                        <p>Snap your shows and upload.</p>
                    </div>
                    <button
                        type='button'
                        className='importShowsChooseButton'
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Choose
                    </button>
                    <input
                        ref={fileInputRef}
                        className='importShowsFileInput'
                        type='file'
                        accept='image/jpeg,image/png,image/webp'
                        multiple
                        onChange={onFilesSelected}
                    />
                    <div className='importShowsPickerHelpRow'>
                        <p className='importShowsPickerHelp'>
                            Choose up to {MAX_LIBRARY_SCREENSHOTS} screenshots from any podcast app.
                        </p>
                        <div className='importShowsExampleControls'>
                            <button
                                type='button'
                                aria-label='Previous example screenshots'
                                onClick={() => scrollExamples(-1)}
                            >
                                <ChevronLeftRounded aria-hidden='true' />
                            </button>
                            <button
                                type='button'
                                aria-label='Next example screenshots'
                                onClick={() => scrollExamples(1)}
                            >
                                <ChevronRightRounded aria-hidden='true' />
                            </button>
                        </div>
                    </div>
                    <div
                        ref={examplesRef}
                        className='importShowsExamples'
                        role='region'
                        aria-label='Example screenshots from podcast apps'
                        tabIndex='0'
                    >
                        {IMPORT_SCREENSHOT_EXAMPLES.map((example) => (
                            <div className='importShowsExample' key={example.name}>
                                <img
                                    className='importShowsExampleScreenshot'
                                    src={example.screenshot}
                                    alt=''
                                    loading='lazy'
                                    draggable='false'
                                />
                                <img
                                    className={[
                                        'importShowsExampleAppIcon',
                                        example.roundAppIcon
                                            ? 'importShowsExampleAppIconRound'
                                            : '',
                                        example.softlyRoundedAppIcon
                                            ? 'importShowsExampleAppIconSoftSquare'
                                            : '',
                                    ].filter(Boolean).join(' ')}
                                    src={example.appIcon}
                                    alt=''
                                    loading='lazy'
                                    draggable='false'
                                />
                            </div>
                        ))}
                    </div>
                </section>

                {selectionError && (
                    <div className='importShowsInlineError' role='alert'>
                        <ErrorOutlineRounded aria-hidden='true' />
                        <span>{selectionError}</span>
                    </div>
                )}

                {selectedFiles.length > 0 && (
                    <section className='importShowsSelection' aria-label='Selected screenshots'>
                        <div className='importShowsSectionHeader'>
                            <h2>Selected</h2>
                            <span>{selectedFiles.length} of {MAX_LIBRARY_SCREENSHOTS}</span>
                        </div>
                        <div className='importShowsSelectedFiles'>
                            {selectedFiles.map((file, index) => (
                                <div
                                    className='importShowsSelectedFile'
                                    key={`${file.name}-${file.lastModified}-${index}`}
                                >
                                    <AddPhotoAlternateRounded aria-hidden='true' />
                                    <span>{file.name}</span>
                                    <button
                                        type='button'
                                        aria-label={`Remove ${file.name}`}
                                        disabled={uploading}
                                        onClick={() => removeSelectedFile(index)}
                                    >
                                        <CloseRounded aria-hidden='true' />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <button
                            type='button'
                            className='importShowsPrimaryButton'
                            disabled={uploading}
                            onClick={uploadScreenshots}
                        >
                            {uploading
                                ? 'Importing…'
                                : `Import ${selectedFiles.length === 1 ? 'screenshot' : 'screenshots'}`}
                        </button>
                    </section>
                )}

                {(uploading || finished || uploadErrors.length > 0) && (
                    <section className='importShowsProgressCard' aria-live='polite'>
                        <div className='importShowsProgressHeader'>
                            <span
                                className={[
                                    'importShowsProgressIcon',
                                    finished && uploadErrors.length === 0
                                        ? 'importShowsProgressIconComplete'
                                        : '',
                                ].filter(Boolean).join(' ')}
                            >
                                {finished && uploadErrors.length === 0
                                    ? <CheckRounded aria-hidden='true' />
                                    : uploadErrors.length > 0 && !uploading
                                        ? <ErrorOutlineRounded aria-hidden='true' />
                                        : <span className='importShowsSpinner' aria-hidden='true' />}
                            </span>
                            <div>
                                <h2>{progressText || 'Could not finish import'}</h2>
                                {uploadTotal > 0 && (
                                    <p>{processedCount} of {uploadTotal} screenshots processed</p>
                                )}
                            </div>
                        </div>
                        {uploadTotal > 0 && (
                            <progress
                                className='importShowsProgress'
                                aria-label='Screenshot import progress'
                                max={uploadTotal}
                                value={processedCount}
                            />
                        )}
                        {uploadErrors.length > 0 && (
                            <div className='importShowsUploadErrors' role='alert'>
                                {uploadErrors.map((error, index) => (
                                    <p key={`${error.filename}-${index}`}>
                                        {error.filename && <strong>{error.filename}: </strong>}
                                        {error.message}
                                    </p>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                {nothingFound && (
                    <div className='importShowsEmptyResult'>
                        No shows were found in these screenshots.
                    </div>
                )}

                {importedPodcasts.length > 0 && (
                    <section className='importShowsResults'>
                        <div className='importShowsSectionHeader'>
                            <h2>Imported {importedPodcasts.length} {importedPodcasts.length === 1 ? 'show' : 'shows'}</h2>
                        </div>
                        <div className='importShowsPodcastRows'>
                            {importedPodcasts.map((podcast) => (
                                <ShowRow key={podcast.sourceId || podcast.name}
                                    source={{ ...podcast, id: podcast.sourceId }} />
                            ))}
                        </div>
                    </section>
                )}

                {unknownPodcastNames.length > 0 && (
                    <section className='importShowsUnknownCard'>
                        <h2>Couldn&apos;t find</h2>
                        <ul>
                            {unknownPodcastNames.map((name) => <li key={name}>{name}</li>)}
                        </ul>
                    </section>
                )}

                {(finished || uploadErrors.length > 0) && !uploading && (
                    <button
                        type='button'
                        className='importShowsClearButton'
                        onClick={clearResults}
                    >
                        Clear results
                    </button>
                )}
            </main>
            <BottomMenu />
        </div>
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
});

const mapActionsToProps = {
    invalidateLatestSubscribed,
    syncSubscribeStatuses,
};

export default connect(mapStateToProps, mapActionsToProps)(ImportShows);
